import { Bell, Moon, Sun, Volume2, VolumeX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/Button";
import { useAlertTTS } from "@/shared/hooks/useAlertTTS";
import { useThemeStore, useResolvedTheme } from "@/shared/hooks/useTheme";
import { LANG_LABEL } from "@/shared/i18n/alertPhrases";
import { Brand } from "@/shared/ui/Brand";
import { LanguageMenu } from "./LanguageMenu";

/**
 * TopBar — search + global actions only. Identity now lives at the
 * bottom of the sidebar (see <AccountMenu />), which is where most
 * modern control-room SaaS surfaces it (Linear, Vercel, Notion).
 */
export function TopBar() {
  const { t } = useTranslation();
  const resolved = useResolvedTheme();
  const toggle = useThemeStore((s) => s.toggle);
  const ThemeIcon = resolved === "dark" ? Moon : Sun;

  const {
    enabled: ttsEnabled,
    setEnabled: setTts,
    supported: ttsSupported,
    lastFailure: ttsFailure,
  } = useAlertTTS();
  const TtsIcon = ttsEnabled ? Volume2 : VolumeX;

  // 조용한 실패 차단: 마지막 발화가 실패했으면 버튼이 그 사실을 말해야 한다.
  // (성공하면 해당 언어 기록이 지워져 표시도 사라진다 — useAlertTTS 참조.)
  const ttsFailed = ttsEnabled && !!ttsFailure;
  const ttsTitle = ttsFailure && ttsFailed
    ? t("widgets.topBar.voiceAlertFailed", {
        lang: LANG_LABEL[ttsFailure.lang],
        reason: ttsFailure.reason,
      })
    : ttsEnabled
      ? t("widgets.topBar.voiceAlertEnabled")
      : t("widgets.topBar.voiceAlertDisabled");

  return (
    <header
      className={
        "sticky top-0 z-30 h-[var(--header-h)] " +
        "bg-[color-mix(in_oklab,var(--bg)_82%,transparent)] backdrop-blur-md " +
        "border-b border-[var(--separator)]"
      }
    >
      <div className="h-full px-5 flex items-center gap-4">
        {/* 상단바는 유틸리티만(언어·테마·음성·알림). 좌측 네비/검색은 두지 않는다 —
            검색은 아직 미구현이라 죽은 입력창을 두느니 비운다.
            다만 **모바일에는 사이드바가 없어 로고가 어디에도 없으므로**
            좌측에 브랜드 마크를 둔다(데스크톱은 사이드바 상단에 이미 있어 중복 방지). */}
        <Brand height={18} className="lg:hidden shrink-0" />

        {/* ── Right cluster (language + theme + alerts) ──────────── */}
        <div className="ml-auto flex items-center gap-1">
          <LanguageMenu />

          <Button
            intent="ghost"
            size="icon"
            onClick={toggle}
            aria-label={resolved === "dark" ? t("widgets.topBar.switchToLight") : t("widgets.topBar.switchToDark")}
            title={resolved === "dark" ? t("widgets.topBar.toLight") : t("widgets.topBar.toDark")}
          >
            <ThemeIcon size={17} strokeWidth={1.75} />
          </Button>

          {ttsSupported && (
            <Button
              intent="ghost"
              size="icon"
              onClick={() => setTts(!ttsEnabled)}
              aria-pressed={ttsEnabled}
              aria-label={ttsEnabled ? t("widgets.topBar.voiceAlertOff") : t("widgets.topBar.voiceAlertOn")}
              title={ttsTitle}
              data-tts-failed={ttsFailed ? "1" : undefined}
              className={
                ttsFailed
                  ? "text-[var(--danger)]"
                  : ttsEnabled
                    ? "text-[var(--accent)]"
                    : undefined
              }
            >
              <TtsIcon size={17} strokeWidth={1.75} />
            </Button>
          )}

          <Button intent="ghost" size="icon" aria-label={t("widgets.topBar.alerts")} title={t("widgets.topBar.alerts")}>
            <Bell size={17} strokeWidth={1.75} />
          </Button>
        </div>
      </div>
    </header>
  );
}
